import React, { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/bbfc07bf-2800-4985-a925-f82e43e5cbf9/images/1137fb4fbd11f73cbcf62c1edd41d56da4c57efd17e6cc43834be691b8faf8f0.png";

export const Hero = ({ onOpenContacts }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobile = () => setMobileNavOpen(false);

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden grain"
      data-testid="hero-section"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      {/* Dark gradient overlays */}
      <div
        className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-[#050505]/40"
        aria-hidden="true"
      />

      {/* Top nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-12 py-4">
        <a href="#hero" className="flex items-center gap-3" data-testid="brand-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_charge-charlie-band/artifacts/hoz25lwc_Charge%20Charlie%20Cover%20Band.webp"
            alt="CHARGE Charlie Cover Band logo"
            className="h-16 w-16 md:h-20 md:w-20 object-contain drop-shadow-[0_0_10px_rgba(0,229,255,0.4)]"
          />
          <span className="hidden sm:block font-display text-base md:text-lg tracking-[0.3em] uppercase text-white font-semibold">
            CHARGE
          </span>
        </a>
        <div className="hidden md:flex items-center gap-8 text-sm tracking-wider uppercase text-white/60">
          <a href="#bio" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-bio">
            Bemutatkozás
          </a>
          <a href="#tagok" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-members">
            Tagok
          </a>
          <a href="#archivum" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-archive">
            Archívum
          </a>
          <a href="#kepek" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-gallery">
            Képek
          </a>
          <a href="#video" className="hover:text-[#00E5FF] transition-colors" data-testid="nav-video">
            Videó
          </a>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-label={mobileNavOpen ? "Menü bezárása" : "Menü megnyitása"}
          aria-expanded={mobileNavOpen}
          data-testid="mobile-menu-toggle"
          className="md:hidden flex h-11 w-11 items-center justify-center border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-colors"
        >
          {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile nav drawer */}
      <div
        className={`md:hidden absolute top-[100px] left-0 right-0 z-30 transition-all duration-300 ease-out ${
          mobileNavOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        data-testid="mobile-menu"
      >
        <div className="mx-4 bg-[#0A0A0A]/95 backdrop-blur-md border border-white/10">
          <div className="h-1 w-full bg-gradient-to-r from-[#00E5FF] via-[#7000FF] to-[#FF007F]" />
          <nav className="flex flex-col p-2">
            {[
              { href: "#bio", label: "Bemutatkozás", testid: "mnav-bio" },
              { href: "#tagok", label: "Tagok", testid: "mnav-members" },
              { href: "#archivum", label: "Archívum", testid: "mnav-archive" },
              { href: "#kepek", label: "Képek", testid: "mnav-gallery" },
              { href: "#video", label: "Videó", testid: "mnav-video" },
            ].map((it) => (
              <a
                key={it.href}
                href={it.href}
                onClick={closeMobile}
                data-testid={it.testid}
                className="px-4 py-3 font-display text-sm tracking-[0.25em] uppercase text-white/80 hover:text-[#00E5FF] hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0"
              >
                {it.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => {
                closeMobile();
                onOpenContacts();
              }}
              data-testid="mnav-contacts"
              className="mt-2 mx-2 mb-2 px-4 py-3 font-display text-sm tracking-[0.25em] uppercase bg-transparent border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition-colors"
            >
              Elérhetőségek
            </button>
          </nav>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 flex flex-col items-start justify-center min-h-[calc(100vh-100px)] px-6 md:px-12 lg:px-20 pb-20">
        <div className="max-w-5xl fade-up">
          <p className="font-display text-xs md:text-sm tracking-[0.4em] uppercase text-[#00E5FF] mb-6 flicker">
            // Cover Band • Magyarország
          </p>
          <h1 className="font-display font-black tracking-tighter text-white leading-[0.85]">
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]">
              CHARGE
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-2 text-white/70">
              Charlie Cover Band
            </span>
          </h1>

          <p className="font-body mt-8 max-w-xl text-base md:text-lg text-white/60 leading-relaxed">
            Élő rock energia. Ismert dalok, autentikus hangzás, koncertről koncertre.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={onOpenContacts}
              data-testid="hero-contacts-button"
              className="group relative inline-flex items-center gap-3 bg-transparent border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.25em] text-xs md:text-sm font-bold pulse-glow"
            >
              <span>Elérhetőségek</span>
              <span className="block h-px w-6 bg-current transition-all group-hover:w-10" />
            </button>
            <a
              href="#video"
              data-testid="hero-watch-button"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors px-6 py-4 uppercase tracking-[0.25em] text-xs md:text-sm"
            >
              Bemutató videó
              <ChevronDown className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Side accent text */}
      <div className="hidden lg:block absolute right-8 bottom-8 z-10 text-right">
        <p className="font-display text-xs tracking-[0.4em] uppercase text-white/40">
          Est. — A ZENE GYÓGYÍT
        </p>
      </div>

      {/* Bottom scroll indicator */}
      <a
        href="#bio"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40 hover:text-[#00E5FF] transition-colors"
        aria-label="Görgetés lefelé"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </a>
    </section>
  );
};

export default Hero;
